import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Play, Pause, ArrowLeft, Path, Lightning, Clock, Gauge } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const riderIcon = new L.DivIcon({
    html: '<div style="background:#1E90FF;width:16px;height:16px;border-radius:50%;border:3px solid #fff;box-shadow:0 0 10px rgba(30,144,255,0.6)"></div>',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    className: '',
});

export default function RideReplay() {
    const { rideId } = useParams();
    const navigate = useNavigate();
    const [replay, setReplay] = useState(null);
    const [loading, setLoading] = useState(true);
    const [playing, setPlaying] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [speed, setSpeed] = useState(1);
    const intervalRef = useRef(null);

    useEffect(() => {
        const fetchReplay = async () => {
            try {
                const res = await fetch(`${API}/rides/${rideId}/replay`, { credentials: 'include', headers: getH() });
                if (res.ok) setReplay(await res.json());
                else toast.error('Ride not found');
            } catch { toast.error('Failed to load ride data'); }
            finally { setLoading(false); }
        };
        fetchReplay();
    }, [rideId]);

    useEffect(() => {
        if (playing && replay?.gps_trace?.length > 1) {
            intervalRef.current = setInterval(() => {
                setCurrentIndex(prev => {
                    if (prev >= replay.gps_trace.length - 1) {
                        setPlaying(false);
                        return prev;
                    }
                    return prev + 1;
                });
            }, 200 / speed);
        }
        return () => clearInterval(intervalRef.current);
    }, [playing, speed, replay]);

    const togglePlay = () => {
        if (currentIndex >= (replay?.gps_trace?.length || 0) - 1) setCurrentIndex(0);
        setPlaying(!playing);
    };

    if (loading) {
        return (
            <Layout>
                <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-[400px] w-full rounded-xl" />
                </div>
            </Layout>
        );
    }

    if (!replay || !replay.gps_trace || replay.gps_trace.length < 2) {
        return (
            <Layout>
                <div className="max-w-4xl mx-auto px-4 py-6">
                    <Card className="glass-card"><CardContent className="p-8 text-center">
                        <Path size={48} className="text-muted-foreground mx-auto mb-4" />
                        <p className="font-medium">No GPS data for this ride</p>
                        <p className="text-sm text-muted-foreground mt-1">Ride replay requires GPS trace data recorded during the ride.</p>
                        <Button onClick={() => navigate('/ride-history')} className="mt-4" variant="outline">
                            <ArrowLeft size={16} className="mr-2" />Back to History
                        </Button>
                    </CardContent></Card>
                </div>
            </Layout>
        );
    }

    const trace = replay.gps_trace;
    const pathCoords = trace.map(p => [p.lat, p.lng]);
    const traveledPath = pathCoords.slice(0, currentIndex + 1);
    const currentPos = pathCoords[currentIndex];
    const progress = ((currentIndex / (trace.length - 1)) * 100).toFixed(0);

    const formatDuration = (s) => {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return `${m}:${sec.toString().padStart(2, '0')}`;
    };

    return (
        <Layout>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                    <Button size="sm" variant="ghost" onClick={() => navigate('/ride-history')} className="mb-3 text-muted-foreground">
                        <ArrowLeft size={16} className="mr-1" />Back
                    </Button>
                    <span className="label-text text-primary text-glow">RIDE REPLAY</span>
                    <h1 className="text-2xl font-bold mt-1 font-display" data-testid="ride-replay-title">Ride Replay</h1>
                </motion.div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-2 my-4">
                    {[
                        { icon: Path, label: 'Distance', val: `${replay.distance.toFixed(2)} km`, color: 'text-primary' },
                        { icon: Clock, label: 'Duration', val: formatDuration(replay.duration), color: 'text-electric-blue' },
                        { icon: Gauge, label: 'Avg Speed', val: `${replay.average_speed} km/h`, color: 'text-cyber-purple' },
                        { icon: Lightning, label: 'Earned', val: `${replay.boltz_earned} Boltz`, color: 'text-neon-gold' },
                    ].map(s => (
                        <Card key={s.label} className="glass-card">
                            <CardContent className="p-3 text-center">
                                <s.icon size={18} className={`${s.color} mx-auto mb-1`} />
                                <p className={`text-sm font-black font-mono ${s.color}`}>{s.val}</p>
                                <p className="text-[10px] text-muted-foreground">{s.label}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Map */}
                <Card className="glass-card overflow-hidden mb-4">
                    <div className="h-[400px]" data-testid="replay-map">
                        <MapContainer
                            center={pathCoords[0]}
                            zoom={15}
                            className="h-full w-full"
                            style={{ background: '#1a1a2e' }}
                        >
                            <TileLayer
                                attribution='&copy; OSM'
                                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                            />
                            {/* Full path (faint) */}
                            <Polyline positions={pathCoords} pathOptions={{ color: '#ffffff20', weight: 3 }} />
                            {/* Traveled path */}
                            <Polyline positions={traveledPath} pathOptions={{ color: '#1E90FF', weight: 4 }} />
                            {/* Start/End markers */}
                            <Marker position={pathCoords[0]}>
                                <Popup>Start</Popup>
                            </Marker>
                            <Marker position={pathCoords[pathCoords.length - 1]}>
                                <Popup>Finish</Popup>
                            </Marker>
                            {/* Rider position */}
                            <Marker position={currentPos} icon={riderIcon} />
                        </MapContainer>
                    </div>
                </Card>

                {/* Controls */}
                <Card className="glass-card">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                            <Button onClick={togglePlay} className="btn-primary" data-testid="replay-play-btn">
                                {playing ? <Pause size={18} /> : <Play size={18} />}
                            </Button>

                            <div className="flex-1">
                                <input
                                    type="range" min={0} max={trace.length - 1} value={currentIndex}
                                    onChange={e => { setCurrentIndex(Number(e.target.value)); setPlaying(false); }}
                                    className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                                    style={{ background: `linear-gradient(to right, #1E90FF ${progress}%, rgba(255,255,255,0.1) 0%)` }}
                                    data-testid="replay-scrubber"
                                />
                            </div>

                            <div className="flex gap-1">
                                {[1, 2, 4].map(s => (
                                    <Button key={s} size="sm" variant={speed === s ? 'default' : 'outline'}
                                        onClick={() => setSpeed(s)} className={`text-xs ${speed === s ? 'btn-primary' : ''}`}
                                        data-testid={`speed-${s}x`}>
                                        {s}x
                                    </Button>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-between mt-2 text-xs text-muted-foreground font-mono">
                            <span>Point {currentIndex + 1} / {trace.length}</span>
                            <span>{progress}% complete</span>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
